import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ChevronRight } from './icons';
import { TransactionRow, transferToTx, swapOrderToTx, incptLedgerToTx, presalePurchaseToTx } from './TransactionRow';
import { useTransfers } from '../../hooks/useTransfers';
import { useCardTransactions } from '../../hooks/useCards';
import { useSwapOrders } from '../../hooks/useSwap';
import { useIncptTransfers } from '../../hooks/useIncptTransfers';
import { usePresale } from '../../hooks/usePresale';
import { useWallet, type WalletAddress } from '../../hooks/useWallet';
import { isSwapDepositTransfer, swapDepositTxHash } from '../../lib/swapHistory';
import { addressDisplayName, shortenAddress } from '../../lib/walletNames';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import type { TransferDto, Chain, Token } from '../../lib/transfers';
import type { SwapOrderDto } from '../../lib/swap';

export function TxHistoryScreen({
  onClose,
  filter,
  title,
}: {
  onClose: () => void;
  /** Restrict to a single chain/token (opened from a coin detail page). */
  filter?: { chain?: Chain; token?: Token };
  title?: string;
}) {
  const { t, i18n } = useTranslation();
  const { wallet } = useWallet();
  const [walletId, setWalletId] = useState<string | null>(null);
  const { transfers, loading } = useTransfers(filter);
  const { txs: cardTxs, loading: cardLoading } = useCardTransactions();
  const { orders: swapOrders, loading: swapLoading } = useSwapOrders(filter);
  const { transfers: incptTransfers, loading: incptLoading } = useIncptTransfers();
  const { purchases, loading: presaleLoading } = usePresale();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();

  const wallets = (wallet?.addresses ?? [])
    .filter((a) => !filter?.chain || a.chain === filter.chain)
    .sort((a, b) => {
      const chainRank = (c: string) => (c === 'trx' ? 0 : 1);
      const byChain = chainRank(a.chain) - chainRank(b.chain);
      if (byChain !== 0) return byChain;
      return a.derivationIndex - b.derivationIndex;
    });
  const selectedWallet = wallets.find((a) => a.id === walletId) ?? null;

  const filteredTransfers = transfers.filter((tr) => !isSwapDepositTransfer(tr, swapOrders));

  const filtered = filteredTransfers.filter((tr) => {
    if (filter && ((filter.chain && tr.chain !== filter.chain) || (filter.token && tr.token !== filter.token))) {
      return false;
    }
    return !selectedWallet || transferHitsWallet(tr, selectedWallet);
  });
  const cryptoRows = filtered.flatMap((tr) => transferToTx(tr, i18n.language, t));
  const swapRows = swapOrders
    .filter((o) => {
      if (filter) {
        const matchFrom =
          (!filter.chain || o.fromChain === filter.chain) &&
          (!filter.token || o.fromToken === filter.token);
        const matchTo =
          (!filter.chain || o.toChain === filter.chain) &&
          (!filter.token || o.toToken === filter.token);
        if (!(matchFrom || matchTo)) return false;
      }
      return !selectedWallet || swapHitsWallet(o, selectedWallet);
    })
    .map((o) => swapOrderToTx(o, i18n.language, t, swapDepositTxHash(o, transfers)));

  const incptRows = filter
    ? []
    : incptTransfers
        .filter(
          (e) =>
            e.kind === 'transfer_out' ||
            e.kind === 'transfer_in' ||
            e.kind === 'admin_grant' ||
            e.kind === 'promocode' ||
            e.kind === 'referral',
        )
        .map((e) => incptLedgerToTx(e, i18n.language, t));

  const purchaseRows = filter ? [] : purchases.map((p) => presalePurchaseToTx(p, i18n.language, t));

  const rows = (filter || selectedWallet
    ? [...cryptoRows, ...swapRows]
    : [...cryptoRows, ...swapRows, ...incptRows, ...purchaseRows, ...cardTxs]
  ).sort((a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0));
  const isLoading = loading || cardLoading || swapLoading || incptLoading || presaleLoading;

  return (
    <div className="relative flex min-h-screen flex-col bg-surface">
      <ScreenBackdrop height={360} fadeStart={28} />
      <div className="relative z-10 flex min-h-screen flex-col px-4 pb-10 pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{title ?? t('wallet.operations')}</h1>
        </header>

        {wallets.length > 1 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <WalletChip active={!selectedWallet} onClick={() => setWalletId(null)}>
              {t('wallet.all')}
            </WalletChip>
            {wallets.map((a) => (
              <WalletChip key={a.id} active={selectedWallet?.id === a.id} onClick={() => setWalletId(a.id)}>
                {addressDisplayName(a, t('nav.wallet'))}
                {!filter?.chain && (
                  <span className={selectedWallet?.id === a.id ? 'text-white/70' : 'text-muted'}>
                    {' '}
                    · {a.chain === 'eth' ? 'ETH' : a.chain === 'btc' ? 'BTC' : 'TRX'}
                  </span>
                )}
                <span className={selectedWallet?.id === a.id ? 'text-white/70' : 'text-muted'}>
                  {' '}
                  {shortenAddress(a.eoaAddress)}
                </span>
              </WalletChip>
            ))}
          </div>
        )}

        {rows.length === 0 ? (
          <div className="mt-24 text-center text-[14px] text-muted">
            {isLoading
              ? t('common.loading')
              : selectedWallet
                ? t('wallet.noWalletTransactions')
                : t('wallet.noTransactions')}
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-[10px]">
            {rows.map((tx, i) => (
              <TransactionRow key={tx.partnerOrderId ?? `${tx.sortTs}-${i}`} tx={tx} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WalletChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3.5 py-2 text-[13px] font-medium ${
        active ? 'bg-ink text-white' : 'bg-white/70 text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function sameAddress(a: string, b: string): boolean {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al.startsWith('0x') || bl.startsWith('0x') || al.startsWith('bc1') || bl.startsWith('bc1')) {
    return al === bl;
  }
  return a === b;
}

function addressHits(addr: WalletAddress, value: string | null | undefined): boolean {
  if (!value) return false;
  if (sameAddress(value, addr.eoaAddress)) return true;
  return !!addr.smartAccountAddress && sameAddress(value, addr.smartAccountAddress);
}

function transferHitsWallet(tr: TransferDto, addr: WalletAddress): boolean {
  return addressHits(addr, tr.fromAddress) || addressHits(addr, tr.toAddress);
}

function swapHitsWallet(order: SwapOrderDto, addr: WalletAddress): boolean {
  return addressHits(addr, order.refundAddress) || addressHits(addr, order.payoutAddress);
}
